import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import WeekCalendarHeader from './week-calendar-header';
import WeekCalendarItem from './week-calendar-item';
import WeekCalendarRow from './week-calendar-row';
import dayjs from 'dayjs';
import { collectStretchableIds, measureStretchLayouts, recomputeFrames } from './week-calendar-layout';
import './week-calendar.css';

const formatHour = (h) => `${String(Math.floor(h)).padStart(2, '0')}:00`;

const WeekCalendar = ({ ...props }) => {
    const { items, startDate, startTime: startMin, finishTime: finishMin, compact, onItemClick, dayPos, setDayPos } =
        props;
    const rootRef = useRef(null);
    const [layoutState, setLayoutState] = useState(null);
    const stretchableIds = useMemo(() => collectStretchableIds(items), [items]);

    const hours = useMemo(() => {
        const list = [];
        for (let h = startMin / 60; h < finishMin / 60; h++) {
            list.push({ hour: h, startMin: Math.round(h * 60) });
        }
        return list;
    }, [startMin, finishMin]);

    const layoutKey = useMemo(
        () =>
            JSON.stringify({
                compact,
                dayPos,
                startMin,
                finishMin,
                start: startDate.valueOf(),
                items: items.map((item) => [
                    item.id,
                    item.startMin,
                    item.finishMin,
                    item.date,
                    item.productName,
                    item.comment,
                    item.roomName,
                    item.sotrName,
                ]),
            }),
        [compact, dayPos, startMin, finishMin, startDate, items]
    );

    const frames = layoutState && layoutState.key === layoutKey ? layoutState.layouts : null;

    const coveredHourStarts = new Set();
    if (compact) {
        const visibleDay = startDate.add(dayPos, 'day').startOf('day');
        items.forEach((item) => {
            if (!stretchableIds.has(item.id)) return;
            if (!dayjs(item.date).startOf('day').isSame(visibleDay)) return;
            hours.forEach(({ startMin: rowStart }) => {
                if (rowStart < item.finishMin && rowStart + 60 > item.startMin) coveredHourStarts.add(rowStart);
            });
        });
    }

    const divRows = [];
    hours.forEach(({ hour, startMin: rowStart }) => {
        const localItems = items.filter((item) => item.startMin >= rowStart && item.startMin < rowStart + 60);
        if (!compact || localItems.length > 0 || coveredHourStarts.has(rowStart)) {
            divRows.push(
                <WeekCalendarRow
                    caption={formatHour(hour)}
                    items={localItems}
                    startDate={startDate}
                    key={`row-${rowStart}`}
                    compact={compact}
                    dayPos={dayPos}
                    onItemClick={onItemClick}
                    hourStartMin={rowStart}
                    frames={frames}
                    stretchableIds={stretchableIds}
                    stretchMeasured={frames != null}
                />
            );
        }
    });

    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        const layouts = measureStretchLayouts(root);
        setLayoutState({ key: layoutKey, layouts });
    }, [layoutKey]);

    useEffect(() => {
        const root = rootRef.current;
        if (!root || typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver(() => {
            setLayoutState((prev) => {
                if (!prev || prev.key !== layoutKey) return prev;
                const layouts = recomputeFrames(root, prev.layouts);
                if (layouts === prev.layouts) return prev;
                return { key: prev.key, layouts };
            });
        });
        observer.observe(root);
        return () => observer.disconnect();
    }, [layoutKey]);

    const divEmpty =
        compact &&
        items.find((item) => dayjs(item.date).startOf('day').isSame(startDate.add(dayPos, 'd').startOf('day'))) ==
            null ? (
            <div className='week-calendar-empty'>Групп нет...</div>
        ) : null;

    const stretchedItems =
        frames &&
        items
            .filter((item) => frames[String(item.id)])
            .map((item) => (
                <WeekCalendarItemOverlay
                    key={`stretch-${item.id}`}
                    item={item}
                    frame={frames[String(item.id)]}
                    startDate={startDate}
                    onItemClick={onItemClick}
                />
            ));

    return (
        <div className='week-calendar' ref={rootRef}>
            <WeekCalendarHeader startDate={startDate} compact={compact} dayPos={dayPos} setDayPos={setDayPos} />
            {divRows}
            {stretchedItems ? <div className='week-calendar-stretch-layer'>{stretchedItems}</div> : null}
            {divEmpty}
        </div>
    );
};

const WeekCalendarItemOverlay = ({ item, frame, startDate, onItemClick }) => {
    const dayIndex = dayjs(item.date).startOf('day').diff(startDate.startOf('day'), 'day');
    return <WeekCalendarItem {...item} dayIndex={dayIndex} frame={frame} onItemClick={onItemClick} />;
};

export default WeekCalendar;
